import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { skillMastersApi, SkillCategory, SkillCategoryNames } from '../services/api';
import './SkillsManagement.css';

function SkillsManagement() {
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingSkill, setEditingSkill] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    category: SkillCategory.Technical,
    description: ''
  });

  useEffect(() => {
    fetchSkills();
  }, [selectedCategory]);

  const fetchSkills = async () => {
    try {
      setLoading(true);
      const categoryFilter = selectedCategory ? parseInt(selectedCategory) : null;
      const data = await skillMastersApi.getAll(categoryFilter);
      setSkills(data);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching skills:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSkill = async (e) => {
    e.preventDefault();
    try {
      await skillMastersApi.create({
        ...formData,
        category: parseInt(formData.category)
      });
      setShowAddModal(false);
      setFormData({ name: '', category: SkillCategory.Technical, description: '' });
      fetchSkills();
    } catch (err) {
      alert(err.message);
      console.error('Error creating skill:', err);
    }
  };

  const handleEditSkill = async (e) => {
    e.preventDefault();
    try {
      await skillMastersApi.update(editingSkill.id, {
        ...formData,
        category: parseInt(formData.category)
      });
      setShowEditModal(false);
      setEditingSkill(null);
      setFormData({ name: '', category: SkillCategory.Technical, description: '' });
      fetchSkills();
    } catch (err) {
      alert(err.message);
      console.error('Error updating skill:', err);
    }
  };

  const handleDeleteSkill = async (id, name) => {
    if (window.confirm(`Are you sure you want to delete the skill "${name}"?`)) {
      try {
        await skillMastersApi.delete(id);
        fetchSkills();
      } catch (err) {
        alert(err.message);
        console.error('Error deleting skill:', err);
      }
    }
  };

  const openEditModal = (skill) => {
    setEditingSkill(skill);
    setFormData({
      name: skill.name,
      category: SkillCategory[skill.category] || SkillCategory.Technical,
      description: skill.description || ''
    });
    setShowEditModal(true);
  };

  const closeModal = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    setEditingSkill(null);
    setFormData({ name: '', category: SkillCategory.Technical, description: '' });
  };

  const filteredSkills = skills.filter(skill =>
    skill.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const groupedSkills = filteredSkills.reduce((acc, skill) => {
    const category = skill.category;
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(skill);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="skills-management-container">
        <div className="loading">Loading skills...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="skills-management-container">
        <div className="error">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="skills-management-container">
      <div className="page-header">
        <h1>Skills Management</h1>
        <p className="subtitle">Manage the master catalog of skills</p>
      </div>

      <div className="controls-bar">
        <div className="search-filter-section">
          <input
            type="text"
            placeholder="Search skills..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="category-filter"
          >
            <option value="">All Categories</option>
            <optgroup label="Professional">
              <option value={SkillCategory.Technical}>Technical</option>
              <option value={SkillCategory.SoftSkills}>Soft Skills</option>
              <option value={SkillCategory.DomainKnowledge}>Domain Knowledge</option>
              <option value={SkillCategory.Tools}>Tools</option>
              <option value={SkillCategory.Languages}>Languages</option>
              <option value={SkillCategory.Certifications}>Certifications</option>
            </optgroup>
            <optgroup label="Personal/Hobbies">
              <option value={SkillCategory.Sports}>Sports</option>
              <option value={SkillCategory.Arts}>Arts</option>
              <option value={SkillCategory.Music}>Music</option>
              <option value={SkillCategory.Cooking}>Cooking</option>
              <option value={SkillCategory.Gaming}>Gaming</option>
              <option value={SkillCategory.Crafts}>Crafts</option>
              <option value={SkillCategory.Outdoor}>Outdoor</option>
              <option value={SkillCategory.Other}>Other</option>
            </optgroup>
          </select>
        </div>
        <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
          + Add Skill
        </button>
      </div>

      <div className="skills-count">
        {filteredSkills.length} skill{filteredSkills.length !== 1 ? 's' : ''} found
      </div>

      <div className="skills-content">
        {Object.keys(groupedSkills).length === 0 ? (
          <div className="no-skills">
            <p>No skills found. Click "Add Skill" to create your first skill.</p>
          </div>
        ) : (
          Object.entries(groupedSkills).map(([category, categorySkills]) => (
            <div key={category} className="category-section">
              <h2 className="category-header">{category}</h2>
              <div className="skills-grid">
                {categorySkills.map((skill) => (
                  <div key={skill.id} className="skill-card">
                    <div className="skill-card-header">
                      <h3>{skill.name}</h3>
                      <div className="skill-actions">
                        <button
                          className="btn-icon"
                          onClick={() => openEditModal(skill)}
                          title="Edit"
                        >
                          ✏️
                        </button>
                        <button
                          className="btn-icon"
                          onClick={() => handleDeleteSkill(skill.id, skill.name)}
                          title="Delete"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                    {skill.description && (
                      <p className="skill-description">{skill.description}</p>
                    )}
                    <div className="skill-footer">
                      <span className="category-badge">{skill.category}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add Skill Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add New Skill</h2>
              <button className="close-button" onClick={closeModal}>×</button>
            </div>
            <form onSubmit={handleAddSkill}>
              <div className="form-group">
                <label htmlFor="name">Skill Name *</label>
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="form-input"
                  placeholder="e.g., React, Python, Photography"
                />
              </div>
              <div className="form-group">
                <label htmlFor="category">Category *</label>
                <select
                  id="category"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  required
                  className="form-input"
                >
                  <optgroup label="Professional">
                    <option value={SkillCategory.Technical}>Technical</option>
                    <option value={SkillCategory.SoftSkills}>Soft Skills</option>
                    <option value={SkillCategory.DomainKnowledge}>Domain Knowledge</option>
                    <option value={SkillCategory.Tools}>Tools</option>
                    <option value={SkillCategory.Languages}>Languages</option>
                    <option value={SkillCategory.Certifications}>Certifications</option>
                  </optgroup>
                  <optgroup label="Personal/Hobbies">
                    <option value={SkillCategory.Sports}>Sports</option>
                    <option value={SkillCategory.Arts}>Arts</option>
                    <option value={SkillCategory.Music}>Music</option>
                    <option value={SkillCategory.Cooking}>Cooking</option>
                    <option value={SkillCategory.Gaming}>Gaming</option>
                    <option value={SkillCategory.Crafts}>Crafts</option>
                    <option value={SkillCategory.Outdoor}>Outdoor</option>
                    <option value={SkillCategory.Other}>Other</option>
                  </optgroup>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="form-input"
                  rows="3"
                  placeholder="Optional description of the skill"
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Add Skill
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Skill Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Skill</h2>
              <button className="close-button" onClick={closeModal}>×</button>
            </div>
            <form onSubmit={handleEditSkill}>
              <div className="form-group">
                <label htmlFor="edit-name">Skill Name *</label>
                <input
                  type="text"
                  id="edit-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="edit-category">Category *</label>
                <select
                  id="edit-category"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  required
                  className="form-input"
                >
                  <optgroup label="Professional">
                    <option value={SkillCategory.Technical}>Technical</option>
                    <option value={SkillCategory.SoftSkills}>Soft Skills</option>
                    <option value={SkillCategory.DomainKnowledge}>Domain Knowledge</option>
                    <option value={SkillCategory.Tools}>Tools</option>
                    <option value={SkillCategory.Languages}>Languages</option>
                    <option value={SkillCategory.Certifications}>Certifications</option>
                  </optgroup>
                  <optgroup label="Personal/Hobbies">
                    <option value={SkillCategory.Sports}>Sports</option>
                    <option value={SkillCategory.Arts}>Arts</option>
                    <option value={SkillCategory.Music}>Music</option>
                    <option value={SkillCategory.Cooking}>Cooking</option>
                    <option value={SkillCategory.Gaming}>Gaming</option>
                    <option value={SkillCategory.Crafts}>Crafts</option>
                    <option value={SkillCategory.Outdoor}>Outdoor</option>
                    <option value={SkillCategory.Other}>Other</option>
                  </optgroup>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="edit-description">Description</label>
                <textarea
                  id="edit-description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="form-input"
                  rows="3"
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default SkillsManagement;
